import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/api';
import { useLang } from '../contexts/LanguageContext';
import { LayoutGrid, Grid2X2 } from 'lucide-react';
import TCGSelector from '../components/catalog/TCGSelector';
import SearchFilters from '../components/catalog/SearchFilters';
import CardGrid from '../components/catalog/CardGrid';
import CardDetailModal from '../components/catalog/CardDetailModal';
import { pickLocalizedText } from '../utils/localizedContent';


export default function CardCatalog() {
  const { lang } = useLang();
  const text = (es, en) => pickLocalizedText(lang, es, en);
  
  // Estado principal
  const [selectedTcg, setSelectedTcg] = useState('pokemon');
  const [displaySize, setDisplaySize] = useState('normal');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filtros
  const [rarityFilter, setRarityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [setFilter, setSetFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Sets disponibles desde la API
  const [availableSets, setAvailableSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);
  
  // Estado del modal
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardBooster, setCardBooster] = useState(null);
  const [cardProducts, setCardProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const searchQueryRef = useRef('');

  // Cargar todos los sets del tipo de TCG actual
  const fetchSets = useCallback(async (tcgType) => {
    setSetsLoading(true);
    try {
      const { data } = await axios.get(`${API}/tcg/sets`, {
        params: { tcg_type: tcgType }
      });
      setAvailableSets(data.sets || []);
    } catch (err) {
      console.error('Error fetching sets:', err);
      setAvailableSets([]);
    } finally {
      setSetsLoading(false);
    }
  }, []);

  // Cargar cartas
  const fetchCards = useCallback(async (tcgType, query, pageNum, rarity, cardType, setId, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const { data } = await axios.get(`${API}/tcg/catalog`, {
        params: { 
          tcg_type: tcgType, 
          q: query, 
          page: pageNum, 
          page_size: 30,
          rarity: rarity || '',
          card_type: cardType || '',
          set_id: setId || ''
        }
      });
      
      if (append) {
        setCards(prev => [...prev, ...(data.cards || [])]);
      } else {
        setCards(data.cards || []);
      }
      setHasMore(data.has_more);
      setTotal(data.total || 0);
      setPage(pageNum);
    } catch (err) {
      // Reintentar una vez tras breve espera ante rate limit (429)
      if (err.response?.status === 429 && !append) {
        const retryAfter = parseInt(err.response?.headers?.['retry-after'] || '3', 10);
        await new Promise(r => setTimeout(r, retryAfter * 1000));
        try {
          const { data } = await axios.get(`${API}/tcg/catalog`, {
            params: { tcg_type: tcgType, q: query, page: pageNum, page_size: 30, rarity: rarity || '', card_type: cardType || '', set_id: setId || '' }
          });
          setCards(data.cards || []);
          setHasMore(data.has_more);
          setTotal(data.total || 0);
          setPage(pageNum);
          return;
        } catch (retryErr) {
          console.error('Retry also failed:', retryErr);
        }
      }
      console.error('Error fetching cards:', err);
      if (!append) setCards([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Cargar sets al cambiar el tipo de TCG
  useEffect(() => {
    fetchSets(selectedTcg);
  }, [selectedTcg, fetchSets]);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  // Cargar cartas al cambiar los filtros
  useEffect(() => {
    fetchCards(selectedTcg, searchQueryRef.current, 1, rarityFilter, typeFilter, setFilter, false);
  }, [fetchCards, selectedTcg, rarityFilter, typeFilter, setFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCards(selectedTcg, searchQuery, 1, rarityFilter, typeFilter, setFilter, false);
  };

  const handleTcgChange = (tcg) => {
    setSelectedTcg(tcg);
    setSearchQuery('');
    setRarityFilter('');
    setTypeFilter('');
    setSetFilter('');
    setCards([]);
    setPage(1);
  };
  
  const clearFilters = () => {
    setRarityFilter('');
    setTypeFilter('');
    setSetFilter('');
  };

  // Scroll infinito — cargar más
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchCards(selectedTcg, searchQuery, page + 1, rarityFilter, typeFilter, setFilter, true);
    }
  }, [loadingMore, hasMore, fetchCards, selectedTcg, searchQuery, page, rarityFilter, typeFilter, setFilter]);

  const handleCardClick = async (card) => {
    setSelectedCard(card);
    setProductsLoading(true);
    setCardProducts([]);
    setCardBooster(null);
    try {
      const params = new URLSearchParams();
      if (card.set_id) params.append('set_id', card.set_id);
      if (card.set_name) params.append('set_name', card.set_name);
      
      const { data } = await axios.get(`${API}/tcg/card/${card.tcg_type}/products?${params.toString()}`);
      setCardBooster(data.booster || null);
      setCardProducts(data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setProductsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 page-enter">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {text('Catálogo de Cartas', 'Card Catalog')}
        </h1>
        <p className="text-[#A1A1AA]">
          {text('Explora todas las cartas y descubre dónde conseguirlas', 'Browse all cards and discover where to get them')}
        </p>
      </div>

      {/* TCG Type Selector */}
      <TCGSelector selectedTcg={selectedTcg} onSelect={handleTcgChange} />

      {/* Search & Filters + Size Toggle */}
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <SearchFilters
            lang={lang}
            selectedTcg={selectedTcg}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={handleSearch}
            rarityFilter={rarityFilter}
            setRarityFilter={setRarityFilter}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            setFilter={setFilter}
            setSetFilter={setSetFilter}
            availableSets={availableSets}
            setsLoading={setsLoading}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            onClearFilters={clearFilters}
            onPageReset={() => setPage(1)}
          />
        </div>
        {/* Display size toggle */}
        <div className="flex items-center gap-1 bg-[#12121A] border border-white/5 rounded-xl p-1 mt-0 flex-shrink-0">
          <button
            onClick={() => setDisplaySize('normal')}
            title={text('Vista normal', 'Normal view')}
            className={`p-2 rounded-lg transition-colors ${displaySize === 'normal' ? 'bg-[#C9A962] text-[#0A1612]' : 'text-[#A1A1AA] hover:text-white'}`}
          >
            <LayoutGrid size={16} />
          </button>
          <button
            onClick={() => setDisplaySize('small')}
            title={text('Vista compacta', 'Compact view')}
            className={`p-2 rounded-lg transition-colors ${displaySize === 'small' ? 'bg-[#C9A962] text-[#0A1612]' : 'text-[#A1A1AA] hover:text-white'}`}
          >
            <Grid2X2 size={16} />
          </button>
        </div>
      </div>

      {/* Card Grid with Infinite Scroll */}
      <CardGrid
        cards={cards}
        loading={loading}
        loadingMore={loadingMore}
        total={total}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onCardClick={handleCardClick}
        lang={lang}
        displaySize={displaySize}
      />

      {/* Card Detail Modal */}
      <CardDetailModal
        card={selectedCard}
        booster={cardBooster}
        products={cardProducts}
        loading={productsLoading}
        onClose={() => setSelectedCard(null)}
        lang={lang}
      />
    </div>
  );
}
